import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAppSelector } from '../../../../../../../../app/hooks';
import { downloadAskAicaFile } from '../../../../../../../../store/dashboard/askAica/apiHelper';
import { LibraryFile } from '../../../../../../../../store/dashboard/askAica/actions';
import { isLenderType } from '../../../../../../../../utils/utils';
import { USER_TYPE } from '../../../../../../../../utils/constants/user';
import { AskAicaFileRef } from '../../hooks/useAskAicaFileActions';

/** Identifies a LibraryFile for diffing/list keys — mnemosyneDocId when persisted, else its download URL. */
export const fileKey = (file: LibraryFile) => file.mnemosyneDocId || file.fileUrl;

/** Data Room deep-link for the "View in Dataroom" action — opens the Archive view for the context org. */
const buildDataroomArchiveUrl = (contextOrgId?: string) => `/investor/app/data-room/${contextOrgId}?tab=dataRoom&fileViewType=archive`;

export interface UseLibraryArgs {
    /** Opens a generated file in the matching in-app viewer (docx/xlsx/pdf). */
    onOpenFile: (file: AskAicaFileRef) => void;
    /** Snapshots a generated file into Reports (docx/xlsx/txt). Absent when the reports.addToReports visibility key is off. */
    onAddFileToReports?: (file: AskAicaFileRef) => void;
}

const toFileRef = (file: LibraryFile): AskAicaFileRef => ({ url: file.fileUrl, name: file.fileName, type: file.fileType });

/**
 * Encapsulates the Ask AICA Library panel's state and behavior: the file list and
 * loading state from redux, the "View in Dataroom" link's visibility gate, and file
 * download/open/add-to-reports.
 */
const useLibrary = ({ onOpenFile, onAddFileToReports }: UseLibraryArgs) => {
    // Download must run under the same context org the file was generated for
    // (the portfolio org from the route) — mirrors MessagesContainer's handleDownloadFile.
    const { id: contextOrgId } = useParams();
    const { files, isLoading } = useAppSelector((state) => state.dashboard.askAicaReducer.library);
    const userData = useAppSelector((state) => state.user.userData);

    // Product decision for this link: hide for LENDER/LENDER_TEAM (isLenderType)
    // AND PARTNER — no PNO-based gating.
    const isLenderOrPartner = isLenderType() || userData?.accountType === USER_TYPE.PARTNER;

    const handleDownloadFile = useCallback(
        async (fileUrl: string, fileName: string) => {
            try {
                await downloadAskAicaFile(fileUrl, fileName, contextOrgId);
            } catch (err) {
                console.error('File download failed:', err);
                toast.error('Failed to download the file. Please try again.');
            }
        },
        [contextOrgId],
    );

    const handleViewInDataroom = useCallback(() => {
        window.open(buildDataroomArchiveUrl(contextOrgId), '_blank');
    }, [contextOrgId]);

    const handleOpenFile = useCallback((file: LibraryFile) => onOpenFile(toFileRef(file)), [onOpenFile]);
    const handleAddFileToReports = useCallback((file: LibraryFile) => onAddFileToReports?.(toFileRef(file)), [onAddFileToReports]);

    return {
        files,
        isLoading,
        isLenderOrPartner,
        handleDownloadFile,
        handleViewInDataroom,
        handleOpenFile,
        handleAddFileToReports,
    };
};

export default useLibrary;
